import { ProductGame } from "@/@types/common";
import { useBalance } from "@/providers/useBalance";
import useMediaQuery from "@/providers/useMediaQuery";
import { device } from "@/style/constants";
import { useI18n } from "@/utils/rioti18n";
import { useLocation } from "@gatsbyjs/reach-router";
import { Trans } from "gatsby-plugin-react-i18next";
import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { Container, SmallerSpinner, StyledRPText } from "@/components/Layout/Riotbar/Plugins/Balance/styles";
import { useXSSOAuth } from "@/providers/useXSSOAuth";
import { VirtualCurrencyCodeByProductGame } from "@/@types/currencies";
import { getIconForProduct } from "@/assets/svgs/CurrencyIcons";

export const Balance = ({ game }: { game?: ProductGame; mobile?: boolean }) => {
  const { balance, isLoading } = useBalance(game);
  const { t } = useI18n();

  if (!game) {
    return null;
  }

  if (isLoading) {
    return (
      <Container data-testid="loading-spinner">
        <SmallerSpinner size="small" />
      </Container>
    );
  }

  const balanceAmount = balance?.[VirtualCurrencyCodeByProductGame[game]];

  // This happens if we get logged out
  if (Number.isNaN(balanceAmount) || balanceAmount == undefined) {
    return null;
  }

  const IconByCurrency = getIconForProduct(game);
  let iconSize = 12;
  switch (game) {
    case ProductGame.Valorant:
      iconSize = 16;
      break;
  }

  return (
    <Container data-testid={`balance-${game}`}>
      <Trans // We need to use Trans here because we interpolate a component into the translation.
        t={t}
        i18nKey={`dbuy.riotbar.balance.amount`}
        values={{ balance: balanceAmount, currency: t(`dbuy.game_currency.${game}`) }}
        components={[<IconByCurrency size={iconSize} key={`currency-icon`} />, <StyledRPText key="text" />]}
      />
    </Container>
  );
};

const createParentElement = (id: string, containerSelector: string, mobile: boolean) => {
  const newEl = document.createElement("div");
  newEl.setAttribute("id", id);
  newEl.setAttribute("style", `display: flex;`);
  const container = document.querySelector(containerSelector);
  if (mobile) {
    container?.appendChild(newEl);
  } else {
    container?.parentElement?.insertBefore(newEl, container);
  }
  return newEl;
};

export const BalancePortal = ({
  game,
  desktopContainer,
  mobileContainer,
}: {
  game?: ProductGame;
  desktopContainer: string;
  mobileContainer: string;
}) => {
  const [mobileEl, setMobileEl] = useState<HTMLElement>();
  const [desktopEl, setDesktopEl] = useState<HTMLElement>();
  const { isLoggedIn } = useXSSOAuth();
  const isDesktop = useMediaQuery(device.laptop);
  const { pathname } = useLocation();

  useEffect(() => {
    if (!desktopContainer && !mobileContainer && !isLoggedIn) {
      return;
    }

    // Desktop
    const desktopElement = document.getElementById(`balance-portal__desktop`);
    if (desktopElement) {
      setDesktopEl(desktopElement);
    } else if (!desktopElement && isDesktop) {
      const newEl = createParentElement("balance-portal__desktop", desktopContainer, false);
      setDesktopEl(newEl);
    }

    // Mobile
    // The mobile element doesn't exist when the riotbar loads, so delaying it here to ensure its around.
    setTimeout(() => {
      const mobileElement = document.getElementById(`balance-portal__mobile`);
      if (mobileElement) {
        setMobileEl(mobileElement);
      } else if (!mobileElement && !isDesktop) {
        const newEl = createParentElement("balance-portal__mobile", mobileContainer, true);
        setMobileEl(newEl);
      }
    }, 100);
  }, [desktopContainer, mobileContainer, isDesktop, pathname]);

  if ((!desktopEl && !mobileEl) || !isLoggedIn || !game) {
    return null;
  }
  return (
    <>
      {desktopEl && ReactDOM.createPortal(isDesktop && <Balance game={game} mobile={false} />, desktopEl)}
      {mobileEl && ReactDOM.createPortal(!isDesktop && <Balance game={game} mobile={true} />, mobileEl)}
    </>
  );
};
